class ProductHoverZoom {
  constructor(productEl, zoomRatio = 2.5) {
    this.product = productEl;
    this.zoomRatio = zoomRatio;
    this.preview = productEl.querySelector('.product-zoom-preview');
    this.mql = window.matchMedia('(min-width: 990px) and (hover: hover)');
    this.lensSize = 140;
    this.activeMedia = null;
    this.activeContainer = null;
    this.activeImage = null;
    this.activeZoomSrc = null;
    this.lens = null;
    this.boundHandlers = new Map();
    this.loadedSources = new Set();

    if (!this.preview) return;

    this.lens = document.createElement('div');
    this.lens.className = 'product-zoom-lens';
    this.lens.setAttribute('aria-hidden', 'true');

    this.init();
  }

  init() {
    this.bindImages();
    this.mql.addEventListener('change', () => this.bindImages());

    const gallery = this.product.closest('media-gallery') || this.product.querySelector('media-gallery');
    gallery?.addEventListener('slideChanged', () => {
      this.hide();
      this.bindImages();
    });

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });
    document.addEventListener('mouseleave', this.onDocumentLeave);
  }

  onScroll = () => {
    if (!this.preview?.classList.contains('is-active') || !this.activeContainer) return;
    this.syncPreviewPosition(this.activeContainer);
  };

  onResize = () => {
    if (!this.preview?.classList.contains('is-active') || !this.activeContainer) return;
    this.syncPreviewPosition(this.activeContainer);
  };

  onDocumentLeave = () => {
    this.hide();
  };

  getDisplaySrc(img) {
    return img.currentSrc || img.src;
  }

  getHiResSrc(img) {
    return img.dataset.zoomSrc || this.getDisplaySrc(img);
  }

  preloadSource(src) {
    if (!src || this.loadedSources.has(src)) return;

    const loader = new Image();
    loader.decoding = 'async';
    loader.onload = () => this.loadedSources.add(src);
    loader.onerror = () => this.loadedSources.add(src);
    loader.src = src;
  }

  preloadImages() {
    if (!this.mql.matches) return;

    this.product
      .querySelectorAll('.product-media-container.media-type-image .product__media img.image-magnify-hover')
      .forEach((img) => {
        this.preloadSource(this.getDisplaySrc(img));
        this.preloadSource(this.getHiResSrc(img));
      });
  }

  bindImages() {
    this.unbindAll();
    if (!this.mql.matches) return;

    this.product
      .querySelectorAll('.product-media-container.media-type-image .product__media img.image-magnify-hover')
      .forEach((img) => {
        const media = img.closest('.product__media');
        const container = img.closest('.product-media-container');
        const slide = img.closest('.product__media-item');
        if (!media || !container || !slide) return;

        const onEnter = (event) => {
          if (!this.isZoomableSlide(slide)) return;
          this.preloadSource(this.getDisplaySrc(img));
          this.preloadSource(this.getHiResSrc(img));
          this.show(event, img, media, container);
        };
        const onMove = (event) => this.move(event, img, media, container);
        const onLeave = () => this.hide(media);

        media.addEventListener('pointerenter', onEnter);
        media.addEventListener('pointermove', onMove);
        media.addEventListener('pointerleave', onLeave);

        this.boundHandlers.set(img, { media, onEnter, onMove, onLeave });
      });

    this.preloadImages();
  }

  unbindAll() {
    this.boundHandlers.forEach(({ media, onEnter, onMove, onLeave }) => {
      media.removeEventListener('pointerenter', onEnter);
      media.removeEventListener('pointermove', onMove);
      media.removeEventListener('pointerleave', onLeave);
    });
    this.boundHandlers.clear();
    this.hide();
  }

  isZoomableSlide(slide) {
    const product = this.product;
    if (product.classList.contains('product--stacked') || product.classList.contains('product--columns')) {
      return true;
    }
    return slide.classList.contains('is-active');
  }

  setPreviewBackground(src) {
    if (!src || this.activeZoomSrc === src) return;
    this.activeZoomSrc = src;
    this.preview.style.backgroundImage = `url("${src}")`;
  }

  show(event, img, media, container) {
    if (!this.mql.matches) return;

    this.activeMedia = media;
    this.activeContainer = container;
    this.activeImage = img;

    if (this.lens.parentElement !== media) {
      media.appendChild(this.lens);
    }

    this.lens.classList.add('is-active');
    this.preview.classList.add('is-active');
    this.preview.setAttribute('aria-hidden', 'false');
    this.syncPreviewPosition(container);

    // Show instantly with the already-loaded display image
    this.setPreviewBackground(this.getDisplaySrc(img));
    this.move(event, img, media, container);

    // Upgrade to hi-res in the background when ready
    const hiResSrc = this.getHiResSrc(img);
    if (hiResSrc !== this.getDisplaySrc(img)) {
      const hiResImage = new Image();
      hiResImage.decoding = 'async';
      hiResImage.onload = () => {
        if (this.activeImage !== img) return;
        this.loadedSources.add(hiResSrc);
        this.setPreviewBackground(hiResSrc);
        this.move(event, img, media, container);
      };
      hiResImage.src = hiResSrc;
    }
  }

  hide(media) {
    if (media && media !== this.activeMedia) return;

    this.lens?.classList.remove('is-active');
    this.preview?.classList.remove('is-active');
    this.preview?.setAttribute('aria-hidden', 'true');
    if (this.preview) {
      this.preview.style.backgroundImage = '';
      this.preview.style.backgroundPosition = '';
      this.preview.style.backgroundSize = '';
    }
    this.activeMedia = null;
    this.activeContainer = null;
    this.activeImage = null;
    this.activeZoomSrc = null;
  }

  syncPreviewPosition(container) {
    const infoWrapper = this.product.querySelector('.product__info-wrapper');
    if (!infoWrapper || !container) return;

    const mediaRect = container.getBoundingClientRect();
    const infoRect = infoWrapper.getBoundingClientRect();

    this.preview.style.top = `${mediaRect.top}px`;
    this.preview.style.left = `${infoRect.left}px`;
    this.preview.style.width = `${infoRect.width}px`;
    this.preview.style.height = `${mediaRect.height}px`;
  }

  move(event, img, media, container) {
    if (!this.preview.classList.contains('is-active')) return;

    const mediaRect = media.getBoundingClientRect();
    const lensSize = this.lensSize;
    const zoom = this.zoomRatio;

    let x = event.clientX - mediaRect.left - lensSize / 2;
    let y = event.clientY - mediaRect.top - lensSize / 2;

    const maxX = Math.max(mediaRect.width - lensSize, 0);
    const maxY = Math.max(mediaRect.height - lensSize, 0);
    x = Math.max(0, Math.min(x, maxX));
    y = Math.max(0, Math.min(y, maxY));

    this.lens.style.width = `${lensSize}px`;
    this.lens.style.height = `${lensSize}px`;
    this.lens.style.transform = `translate(${x}px, ${y}px)`;

    this.preview.style.backgroundSize = `${mediaRect.width * zoom}px ${mediaRect.height * zoom}px`;
    this.preview.style.backgroundPosition = `-${x * zoom}px -${y * zoom}px`;

    this.syncPreviewPosition(container);
  }
}

function enableZoomOnHover(zoomRatio = 2.5) {
  document.querySelectorAll('product-info[data-zoom-on-hover] .product').forEach((product) => {
    if (product._hoverZoom) {
      product._hoverZoom.bindImages();
      return;
    }
    product._hoverZoom = new ProductHoverZoom(product, zoomRatio);
  });
}

window.enableZoomOnHover = enableZoomOnHover;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => enableZoomOnHover(2.5));
} else {
  enableZoomOnHover(2.5);
}

